import { ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AttributionWatermark } from '../components/AttributionWatermark'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { type KycStatusGlyph, KycStatusIcon } from '../icons/KycStatusIcon'
import { Box, Text, useTheme } from '../theme/restyle'
import { FiatBackButton } from './FiatBackButton'
import {
  FiatKycOptionRow,
  type FiatKycOptionRowProps,
} from './FiatKycOptionRow'

export interface FiatKycOptionsScreenProps {
  /** The card's own glyph, which need not be the banner's. */
  glyph: KycStatusGlyph
  title: string
  /** Heading over the routes, e.g. "Right now you can:". */
  optionsLabel: string
  options: FiatKycOptionRowProps[]
  onBack: () => void
}

/**
 * What the buyer can still do with a deposit identity is holding (Figma KYC
 * R2, 2387:13322, 2387:13488 and 2387:13602).
 *
 * Distinct from {@link FiatKycOutcomeScreen}, which reports the verdict as
 * soon as the form is submitted. This one is reached later, from the deposit
 * the buyer came back to.
 */
export function FiatKycOptionsScreen({
  glyph,
  title,
  optionsLabel,
  options,
  onBack,
}: FiatKycOptionsScreenProps) {
  const theme = useTheme()
  const { height } = useFlowStepFrame()
  const insets = useSafeAreaInsets()

  return (
    <Box
      style={[
        styles.screen,
        { height, backgroundColor: theme.background.modal },
      ]}
    >
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top, paddingBottom: insets.bottom + 20 },
        ]}
      >
        <FiatBackButton onPress={onBack} />
        <Box style={styles.glyph}>
          {/* The card washes the glyph from brand to text colour, where the
              banner draws it at one strength. */}
          <KycStatusIcon glyph={glyph} faded height={GLYPH_HEIGHT[glyph]} />
        </Box>
        <Text
          weight="medium"
          style={[styles.title, { color: theme.content.elements.primary }]}
        >
          {title}
        </Text>
        {/* The frames leave the same slack above the label as below the last
            route, so the block floats rather than sitting on the watermark. */}
        <Box flex={1} style={styles.slack} />
        <Text
          weight="medium"
          style={[styles.label, { color: theme.content.elements.secondary }]}
        >
          {optionsLabel}
        </Text>
        <Box style={styles.options}>
          {options.map((option) => (
            <FiatKycOptionRow key={option.title} {...option} />
          ))}
        </Box>
        <Box flex={1} style={styles.slack} />
        <Box style={styles.attribution}>
          <AttributionWatermark />
        </Box>
      </ScrollView>
    </Box>
  )
}

/** 51 in the frames that carry a person glyph, 61 where it carries a clock. */
const GLYPH_HEIGHT: Record<KycStatusGlyph, number> = {
  complete: 51,
  pending: 61,
  soft: 51,
  hard: 51,
  required: 51,
}

const styles = StyleSheet.create({
  screen: { overflow: 'hidden' },
  content: { flexGrow: 1 },
  // A fixed slot, so the headline starts at the same place whichever glyph
  // the outcome brings.
  glyph: { marginTop: 90, marginLeft: 31, height: 72 },
  title: {
    marginHorizontal: 31,
    // Held to the width the frames give it, so it breaks where they break.
    maxWidth: 309,
    fontSize: 24,
    lineHeight: 27,
    letterSpacing: -0.48,
  },
  slack: { minHeight: 40 },
  label: {
    marginHorizontal: 31,
    fontSize: 17,
    lineHeight: 19,
    letterSpacing: -0.34,
  },
  options: { marginTop: 13, marginHorizontal: 24, gap: 8 },
  attribution: { alignItems: 'center' },
})
